import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { BiMoviePlay } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { 
  acceptFriendRequest, 
  cancelFriendRequest, 
  getSuggestedFriends, 
  sendFriendRequest 
} from "../redux/friend/friendAction";
import { toggleInvite } from "../redux/friend/friendReducer";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import BD_Gabriel from "../assests/defaultImage.png";
import profileImage from '../assests/defaultImage.png';
import RealsAdvert from "./realsModule/reals-advert";

// Animation Components
const AnimatedSection = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.4, delay, ease: "easeOut" }}
    className={className}
    layout
  >
    {children}
  </motion.div>
);

const LoadingSkeleton = () => (
  <div className="space-y-3">
    {[...Array(4)].map((_, index) => (
      <div key={index} className="flex items-center space-x-3 animate-pulse">
        <div className="w-10 h-10 bg-gray-600 rounded-full"></div>
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-gray-600 rounded w-3/4"></div>
          <div className="h-2 bg-gray-600 rounded w-1/2"></div>
        </div>
        <div className="w-16 h-8 bg-gray-600 rounded-md"></div>
      </div>
    ))}
  </div>
);

// Reels Advert Section
const ReelsAdvertSection = () => {
  const [showReels, setShowReels] = useState(false);
  
  const toggleReels = useCallback(() => {
    setShowReels(prev => !prev);
  }, []);

  const disableReels = useCallback(() => {
    setShowReels(false);
  }, []);

  return (
    <div className="flex justify-end mb-6">
      <AnimatePresence mode="wait">
        {!showReels ? (
          <motion.button
            key="reels-button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="text-3xl self-end text-end text-customGold hover:text-gold-400 transition-colors"
            onClick={toggleReels}
            title="Watch Flips"
          >
            <BiMoviePlay />
          </motion.button>
        ) : (
          <motion.div
              key="reels-content"
              className="w-full"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <RealsAdvert disable={disableReels} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// User Item Component
export const UserItem = React.memo(({ user }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { userData } = useSelector((state) => state.user);
  
  const { 
    firstName, 
    lastName, 
    profilePhoto, 
    userId, 
    friendshipStatus,
    userName
  } = user ?? {};

  const myId = userData?.userId;
  const avatar = profilePhoto?.[0]?.photoPublicUrl || BD_Gabriel;

  // console.log ("user",  user)
  
  const friendshipConfig = useMemo(() => {
    const configs = {
      "User are pals": { text: "Pals", style: "bg-customGold text-black" },
      "you sent a request to this user": { text: "Invite Sent", style: "bg-customGold text-black", action: "cancel" },
      "you have a request from this user": { text: "Accept", style: "bg-green-600 text-white", action: "accept" },
      default: { text: "Send Invite", style: "bg-customDark text-white", action: "send" }
    };
    
    return configs[friendshipStatus] || configs.default;
  }, [friendshipStatus]);

  const handleUserAction = async () => {
    if (!userId || !myId || isLoading) return;

    try {
      setIsLoading(true);
      
      switch (friendshipConfig.action) {
        case 'cancel':
          await dispatch(cancelFriendRequest({ userId: myId, friendId: userId })).unwrap();
          dispatch(toggleInvite({ userId, type: 'remove' }));
          break;
        case 'accept':
          await dispatch(acceptFriendRequest({ userId: myId, friendId: userId })).unwrap();
          dispatch(toggleInvite({ userId, type: 'accept' }));
          break;
        case 'send':
          await dispatch(sendFriendRequest({ userId: myId, friendId: userId })).unwrap();
          dispatch(toggleInvite({ userId, type: 'add' }));
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Friend action failed:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileClick = () => {
    navigate(`/user/${userId}`);
  };

  return (
    <motion.div
      className="border-b border-gray-600 py-3 last:border-b-0 hover:bg-gray-700/30 rounded-lg px-2 transition-colors"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-center justify-between space-x-3">
        {/* Profile Image */}
        <button 
          onClick={handleProfileClick}
          className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden border-2 border-customGold hover:border-gold-400 transition-colors"
        >
          <img
            src={avatar}
            alt={`${firstName} ${lastName}`}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </button>

        {/* User Info */}
        <div 
          className="flex flex-col flex-1 min-w-0 cursor-pointer" 
          onClick={handleProfileClick}
        >
          <p 
            className="text-sm font-medium text-white truncate"
            title={`${firstName} ${lastName}`}
          >
            {firstName} {lastName}
          </p>
          {userName && (
            <span className="text-xs text-gray-400 truncate">BD@{userName}</span>
          )}
        </div>

        {/* Action Button */}
        <motion.button
          className={`px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 min-w-[80px] ${
            friendshipConfig.style
          } ${isLoading ? "opacity-50 cursor-not-allowed" : "hover:shadow-lg"}`}
          onClick={handleUserAction}
          disabled={isLoading}
          whileHover={!isLoading ? { scale: 1.05 } : {}}
          whileTap={!isLoading ? { scale: 0.95 } : {}}
        >
          {isLoading ? "..." : friendshipConfig.text}
        </motion.button>
      </div>
    </motion.div>
  );
});

// Chat Component
const Chat = React.memo(() => {
  const navigate = useNavigate();
  const { chatList } = useSelector(state => state.chat);
  const chatRooms = useSelector(state => state.chat.chatRoom);
  const { userData } = useSelector(state => state.user);
  const myId = userData?.userId;

  const sortedChatList = useMemo(() => {
    return [...chatList].sort((a, b) => {
      const a_id = `${a.user.userId}_${myId}`;
      const b_id = `${b.user.userId}_${myId}`;

      const chatRoomA = chatRooms[a_id];
      const chatRoomB = chatRooms[b_id];

      const getLastMessageTime = (chatRoom) => {
        const message = chatRoom?.messages?.[0];
        return message?.localCreatedAt || message?.createdAt || 0;
      };

      return new Date(getLastMessageTime(chatRoomB)) - new Date(getLastMessageTime(chatRoomA));
    });
  }, [chatList, chatRooms, myId]);

  const handleChatClick = (user) => {
    navigate('/chat', { state: { user } });
  };

  if (sortedChatList.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <BiMoviePlay className="text-4xl mx-auto mb-2 opacity-50" />
        <p className="text-sm">No messages yet</p>
      </div>
    );
  }

  return (
    <div className="">
      {sortedChatList.map((chat, index) => {
        const { user, unreadCount, typing } = chat ?? {};
        const { profilePhoto, firstName, lastName, userId } = user ?? {};
        const avatar = profilePhoto?.[0]?.photoPublicUrl || profileImage;

        return (
          <motion.button
            key={`${userId}-${index}`}
            onClick={() => handleChatClick(user)}
            className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-gray-700/50 transition-colors group"
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="relative flex-shrink-0 w-12 h-12">
                <img
                  src={avatar}
                  className="object-cover w-full h-full rounded-full border-2 border-gray-500 group-hover:border-customGold transition-colors"
                  alt={`${firstName} ${lastName}`}
                />
                {unreadCount > 0 && (
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold border-2 border-[#353530]"
                  >
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </motion.div>
                )}
              </div>

              <div className="flex flex-col items-start flex-1 min-w-0">
                <p className="text-sm font-medium text-white text-left truncate w-full">
                  {firstName} {lastName}
                </p>
                <p className={`text-xs text-left truncate w-full ${
                  typing ? "text-customGold font-medium" : "text-gray-400"
                }`}>
                  {typing || "Tap to chat"}
                </p>
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
});

// Advertisement Data
const advertisements = [
  {
    type: "image",
    src: "https://media.licdn.com/dms/image/v2/C4E1BAQH5ZNEox9-dGg/company-background_10000/company-background_10000/0/1585279282611/foodconigeriaco_cover?e=2147483647&v=beta&t=akjW3kM45yFM6TuFndUpW7n3hpGKjdeo4-aS__k3M-8",
    companyName: "FoodCo",
    location: "Ikeja, Lagos",
    catchphrase: "Fresh groceries at unbeatable prices!",
  },
  {
    type: "video",
    src: "https://samplelib.com/lib/preview/mp4/sample-5s.mp4",
    companyName: "Luxury Cars",
    location: "Victoria Island, Lagos",
    catchphrase: "Drive the future today! Check our showroom.",
  },
  {
    type: "image",
    src: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQtY6DSILJocOONCdjmFcQyPog5FN6k_qMPww&s",
    companyName: "Swift Laundry",
    location: "Ibadan, Oyo",
    catchphrase: "Affordable and fast laundry services near you.",
  },
];

// Advertisement Component
const AdvertisementSection = () => {
  const [currentAdIndex, setCurrentAdIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAdIndex(prev => (prev + 1) % advertisements.length);
    }, 8000); // Increased to 8 seconds for better UX

    return () => clearInterval(interval);
  }, []);

  const currentAd = advertisements[currentAdIndex];

  return (
    <AnimatedSection className="bg-[#353530] p-4 rounded-2xl shadow shadow-black/80">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">Sponsored</h3>
        <div className="flex space-x-1">
          {advertisements.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentAdIndex ? "bg-customGold" : "bg-gray-500"
              }`}
              onClick={() => setCurrentAdIndex(index)}
            />
          ))}
        </div>
      </div>

      <div className="relative w-full aspect-square overflow-hidden rounded-xl mb-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentAdIndex}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 w-full h-full"
          >
            {currentAd.type === "image" ? (
              <img
                src={currentAd.src}
                alt={currentAd.companyName}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <video
                src={currentAd.src}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-center"
      >
        <h4 className="text-lg font-bold text-customGold mb-1">
          {currentAd.companyName}
        </h4>
        <p className="text-gray-400 text-sm mb-2">{currentAd.location}</p>
        <p className="text-white text-sm leading-relaxed">
          {currentAd.catchphrase}
        </p>
      </motion.div>
    </AnimatedSection>
  );
};


const RightSidebar = () => {
  const dispatch = useDispatch();
  const { suggestFriends, loading } = useSelector(state => state.friend);
  const [visibleUsersCount, setVisibleUsersCount] = useState(4);
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      dispatch(getSuggestedFriends());
    }
  }, [dispatch]);

  const loadMoreUsers = () => {
    setVisibleUsersCount(prev => prev + 4);
  };

  const visibleUsers = useMemo(() => 
    suggestFriends.slice(0, visibleUsersCount), 
    [suggestFriends, visibleUsersCount]
  );

  const canLoadMore = visibleUsersCount < suggestFriends.length;

  return (
      <motion.div
        initial={false}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className={`w-full p-4`}
      >

        <div className="space-y-4">
          <ReelsAdvertSection />
          
          <AdvertisementSection />

          {/* Suggested Accounts */}
          <AnimatedSection delay={0.1} className="bg-[#353530] p-4 rounded-2xl shadow shadow-black/80">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-white">Suggested Accounts</h3>
              <span className="text-xs text-gray-400 bg-gray-600 px-2 py-1 rounded-full">
                {suggestFriends.length}
              </span>
            </div>

            {loading ? (
              <LoadingSkeleton />
            ) : (
              <>
                <div className="space-y-2">
                  {visibleUsers.map((user, index) => (
                    <UserItem 
                      key={user.userId} 
                      user={user}
                    />
                  ))}
                </div>

                {canLoadMore && (
                  <motion.button
                    onClick={loadMoreUsers}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full mt-4 py-2 text-sm text-customGold border border-customGold rounded-lg hover:bg-customGold/10 transition-colors"
                  >
                    Load More
                  </motion.button>
                )}
              </>
            )}
          </AnimatedSection>

          {/* Messages Section */}
          <AnimatedSection delay={0.2} className="bg-[#353530] py-4 rounded-2xl shadow shadow-black/80">
            <div className="flex items-center px-4 justify-between">
              <h3 className="text-sm font-bold text-white">Recent Chats</h3>
              <Link 
                to="/chat" 
                className="text-xs text-customGold hover:text-gold-400 transition-colors"
              >
                View All
              </Link>
            </div>
            <Chat />
          </AnimatedSection>
        </div>
      </motion.div>
  );
};

export default React.memo(RightSidebar);